import StatusBadge from "@/app/components/common/badge/StatusBadge";
import { useT } from "@/app/hooks/useT";
import type { PaymentTransaction } from "@/app/types/payments";
import { Constants } from "@/app/utils/AppConstants";
import { formatDate } from "@/app/utils/DateFormater";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import { SvgIcon } from "@progress/kendo-react-common";
import { chevronDownIcon, chevronUpIcon } from "@progress/kendo-svg-icons";
import { useState } from "react";

interface DetailsProp {
  transaction: PaymentTransaction;
  schedule: boolean;
}

export const Details = ({ transaction, schedule }: DetailsProp) => {
  const t = useT();
  const [isTransactionDetailsOpen, setIsTransactionDetailsOpen] =
    useState<boolean>(true);

  return (
    <div className="gx-transaction-detail-card">
      <button
        type="button"
        className="gx-transaction-detail-card-header"
        onClick={() => setIsTransactionDetailsOpen(!isTransactionDetailsOpen)}
      >
        <h2 className="gx-transaction-detail-card-title gx-h3-b">{t("transaction_details")}</h2>
        <SvgIcon
          icon={isTransactionDetailsOpen ? chevronUpIcon : chevronDownIcon}
          size="small"
        />
      </button>
      {isTransactionDetailsOpen && (
        <div className="gx-transaction-detail-card-body">
          {transaction?.paymentTypeKey === Constants.swiftPay ? (
            <>
              <div className="gx-transaction-detail-row">
                <span className="gx-t-lable">{t("you_send")}</span>
                <span className="gx-t-value">
                  {
                    gxCurrencyFormat(
                      transaction?.debitAmount,
                      transaction?.debitCurrency,
                    ).formatted
                  }
                </span>
              </div>
              <div className="gx-transaction-detail-row">
                <span className="gx-t-lable">{t("they_receive")}</span>
                <span className="gx-t-value">
                  {
                    gxCurrencyFormat(
                      transaction?.beneAmount,
                      transaction?.beneCurrency,
                    ).formatted
                  }
                </span>
              </div>
            </>
          ) : (
            <div className="gx-transaction-detail-row">
              <span className="gx-t-lable">{t("payment_amount")}</span>
              <span className="gx-t-value">
                {
                  gxCurrencyFormat(
                    transaction?.debitAmount,
                    transaction?.debitCurrency,
                  ).formatted
                }
              </span>
            </div>
          )}
          {transaction?.paymentTypeKey === Constants.swiftPay && (
            <div className="gx-transaction-detail-row">
              <span className="gx-t-lable">{t("fx_rate")}</span>
              <span className="gx-t-value">
                {transaction?.forexRate || "---"}
              </span>
            </div>
          )}
          <div className="gx-transaction-detail-row">
            <span className="gx-t-lable">{t("upi_transaction_id")}</span>
            <span className="gx-t-value">{"---"}</span>
          </div>
          <div className="gx-transaction-detail-row">
            <span className="gx-t-lable">{t("purpose_of_payment")}</span>
            <span className="gx-t-value">
              {transaction?.purposeCodeName === "N/A"
                ? "---"
                : transaction?.purposeCodeName}
            </span>
          </div>
          <div className="gx-transaction-detail-row">
            <span className="gx-t-lable">{t("payment_details")}</span>
            <span className="gx-t-value">
              {transaction?.paymentDetails || "---"}
            </span>
          </div>
          <div className="gx-transaction-detail-row">
            <span className="gx-t-lable">{t("payment_reference")}</span>
            <span className="gx-t-value">
              {transaction?.paymentReference || "---"}
            </span>
          </div>
          <div className="gx-transaction-detail-row">
            <span className="gx-t-lable">{t("payment_category")}</span>
            <span className="gx-t-value">
              {transaction?.paymentCategory || "---"}
            </span>
          </div>
          {transaction?.chargeAmount !== 0 && (
            <div className="gx-transaction-detail-row">
              <span className="gx-t-lable">{t("charge_amount")}</span>
              <span className="gx-t-value">
                {
                  gxCurrencyFormat(
                    transaction?.chargeAmount,
                    transaction?.debitCurrency,
                  ).formatted
                }
              </span>
            </div>
          )}
          {transaction?.chargeBearerType && (
            <div className="gx-transaction-detail-row">
              <span className="gx-t-lable">{t("who_pays_charges")}</span>
              <span className="gx-t-value">
                {transaction?.chargeBearerType || "---"}
              </span>
            </div>
          )}

          <div className="gx-transaction-detail-row">
            <span className="gx-t-lable">{t("growx_reference")}</span>
            <span className="gx-t-value">{transaction?.gxChnNo || "---"}</span>
          </div>

          {schedule !== true && Boolean(transaction?.paymentStatusCode) && (
            <div className="gx-transaction-detail-row">
              <span className="gx-t-lable">{t("status")}</span>
              <span className="gx-t-value">
                <StatusBadge
                  module="payments"
                  statusCode={transaction?.paymentStatusCode}
                />
              </span>
            </div>
          )}

          {/* Recurring Details - Only show if enabled */}
          {transaction?.soEnableFlag === "Y" && (
            <>
              <div className="gx-subsection-header">
                <h2 className="gx-subsection-title gx-p14-b">
                  {t("recurring_details")}
                </h2>
              </div>
              <div className="gx-transaction-detail-row">
                <span className="gx-t-lable">{t("start_date")}</span>
                {transaction?.soStartDate && (
                  <span className="gx-t-value">
                    {formatDate(transaction?.soStartDate)}
                  </span>
                )}
              </div>
              <div className="gx-transaction-detail-row">
                <span className="gx-t-lable">{t("end_date")}</span>
                {transaction?.soExpiryDate && (
                  <span className="gx-t-value">
                    {formatDate(transaction?.soExpiryDate)}
                  </span>
                )}
              </div>
              <div className="gx-transaction-detail-row">
                <span className="gx-t-lable">{t("frequency")}</span>
                <span className="gx-t-value">
                  {transaction?.soExeFrequency || "---"}
                </span>
              </div>
              {transaction?.soExeDay && (
                <div className="gx-transaction-detail-row">
                  <span className="gx-t-lable">{t("execution_days")}</span>
                  <span className="gx-t-value">{transaction?.soExeDay}</span>
                </div>
              )}
            </>
          )}

          {/* Internal Remarks */}
          {transaction?.paymentInternalRemarks && (
            <div className="gx-internal-remarks">
              <span className="gx-p14-b">{t("internal_remarks")}</span>
              <p className="gx-p14">{transaction?.paymentInternalRemarks}</p>
            </div>
          )}

          {/* Beneficiary Notification */}
          {transaction?.beneNotifyFlag === "Y" &&
            transaction?.beneNotifyMaskEmailId && (
              <div className="gx-internal-remarks">
                <p>
                  {t(" information_beneficiary_via_email")}{" "}
                  <span className="gx-p14-b">
                    {transaction?.beneNotifyMaskEmailId}
                  </span>
                  <span>.</span>
                </p>
              </div>
            )}

          {transaction?.cbsErrorStatusDesc && (
            <div className="gx-internal-remarks">
              {/* <span className="gx-p14-b">{t("internal_remarks")}</span> */}
              <span className="gx-p14-md datepicker-validation-message">
                {transaction?.cbsErrorStatusDesc}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
